import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory, useLocation } from "react-router";
import moment from "moment";

import { useCompanyStore } from "web-apps/company/stores/companyStore/companyStore";
import { FontFamily } from "config/font";
import { ButtonStyled } from "components/buttons/buttons.styled";
import { DetailsContainer, Cell } from "./obSettings.styled";
import { H3, Text } from "components/Typography/text.styled";
import { CustomIcon } from "components/icon/customIcon.component";
import { Color } from "config/colors";
import { Api } from "services/api/api.service";
import { ObSettingsForm } from "./obSettingsForm";
import { OBSettingShortWeekDay } from "model/Company";
import { isSettingEnabled } from "model/settings.logic";

type Props = {
  view?: string;
};

type DisplaySettingList = {
  id: number[];
  startTime: string;
  endTime: string;
  weekday: number | null;
  date: string | null;
  companyId: number;
  percentValue: number | null;
  fixedValue: number | null;
};

export const ObSettings: React.FC<Props> = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const location = useLocation();

  const [showObForm, setShowObForm] = useState(false);
  const [companyState, , companyDispatch] = useCompanyStore();

  const [settingList, setSettingList] = useState<DisplaySettingList[]>([]);
  const [dateList, setDateList] = useState<DisplaySettingList[][]>([]);
  const [weekList, setWeekList] = useState<DisplaySettingList[][]>([]);

  const haveOBModule = useMemo(() => {
    return isSettingEnabled(
      companyState.companySettings,
      "CompanyShowInconvenientModule",
    );
  }, [companyState.companySettings]);

  useEffect(() => {
    if (haveOBModule) return;

    const params = new URLSearchParams(location.search);
    params.set("page", "Subscription");

    history.replace({
      pathname: location.pathname,
      search: params.toString(),
    });
  }, [haveOBModule, history, location.pathname, location.search]);

  useEffect(() => {
    if (!haveOBModule) return;
    displayOvernightSetting();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [haveOBModule, companyState.obSettings]);

  useEffect(() => {
    if (!haveOBModule) return;
    sortSettingsList(settingList);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [haveOBModule, settingList]);

  const sortSettingsList = (list: DisplaySettingList[]) => {
    const grouped: DisplaySettingList[][] = [];

    list.forEach((setting) => {
      const sameValueIndex = grouped.findIndex((group) => {
        const g0 = group[0];
        return (
          g0.fixedValue === setting.fixedValue &&
          setting.startTime === g0.startTime &&
          setting.endTime === g0.endTime &&
          setting.weekday !== null &&
          g0.weekday !== null
        );
      });

      if (sameValueIndex !== -1)
        grouped[sameValueIndex] = [...grouped[sameValueIndex], setting];
      else grouped.push([setting]);
    });

    const dates = grouped.filter((group) => group[0]?.date !== null);
    setDateList(dates);

    const weekDays = grouped.filter((group) => group[0]?.weekday !== null);

    const sortByWeekday: DisplaySettingList[][] = weekDays
      .map((sameSettingList) => {
        const copy = [...sameSettingList];
        copy.sort((a, b) => {
          if (a.weekday === null || b.weekday === null) return 0;
          if (a.weekday === 0) return 1;
          if (b.weekday === 0) return -1;
          return a.weekday - b.weekday;
        });
        return copy;
      })
      .filter((x) => Array.isArray(x) && x.length > 0);

    setWeekList(sortByWeekday);
  };

  const displayOvernightSetting = () => {
    const nightTimeOb: DisplaySettingList[] = [];
    const dayTimeOb: DisplaySettingList[] = [];

    (companyState.obSettings ?? []).forEach((currentSetting: any) => {
      const foundNightTime = (companyState.obSettings ?? []).find(
        (setting: any) => {
          const m = moment(currentSetting.endTime, "HH:mm:ss");
          const m2 = moment(setting.startTime, "HH:mm:ss").subtract(
            2,
            "seconds"
          );

          const dateBefore = moment(setting.date, "YYYY-MM-DD").subtract(
            1,
            "days"
          );
          const usingDateOverNight =
            Boolean(setting.date) &&
            dateBefore.format("YYYY-MM-DD") === currentSetting.date;

          return (
            ((setting.weekday !== null &&
              currentSetting.weekday !== null &&
              setting.weekday - 1 === currentSetting.weekday) ||
              (setting.weekday !== null &&
                currentSetting.weekday !== null &&
                setting.weekday === 0 &&
                currentSetting.weekday === 6) ||
              usingDateOverNight) &&
            (setting.percentValue === currentSetting.percentValue ||
              currentSetting.fixedValue === setting.fixedValue) &&
            m.format("HH:mm:ss") === m2.format("HH:mm:ss")
          );
        }
      );

      if (foundNightTime) {
        nightTimeOb.push({
          id: [foundNightTime.id, currentSetting.id],
          weekday: currentSetting.weekday,
          date: currentSetting.date,
          startTime: currentSetting.startTime,
          endTime: foundNightTime.endTime,
          companyId: currentSetting.companyId,
          fixedValue: currentSetting.fixedValue,
          percentValue: currentSetting.percentValue,
        });
      }
    });

    (companyState.obSettings ?? []).forEach((obSetting: any) => {
      const merged = nightTimeOb.some((n) => n.id.includes(obSetting.id));
      if (!merged) dayTimeOb.push({ ...obSetting, id: [obSetting.id] });
    });

    setSettingList([...dayTimeOb, ...nightTimeOb]);
  };

  const deleteObSetting = async (sameSettingsList: DisplaySettingList[]) => {
    const removedIds: number[] = [];

    await Promise.all(
      sameSettingsList.map(async (setting) => {
        await Promise.all(
          setting.id.map(async (id) => {
            if (companyState.company && setting?.id.length > 0) {
              await Api()
                .company.deleteCompanyObSettings(companyState.company?.id, id)
                .then(() => {
                  removedIds.push(id);
                })
                .catch((err) => {
                  // eslint-disable-next-line no-console
                  console.log(err, err?.response);
                });
            }
          })
        );
      })
    );

    const newSettings = (companyState.obSettings ?? []).filter(
      (currentSetting: any) => {
        if (currentSetting?.id) return !removedIds.includes(currentSetting.id);
        return true;
      }
    );

    companyDispatch({
      type: "UPDATE_COMPANY_OBSETTINGS",
      payload: newSettings,
    });
  };

  if (!haveOBModule) return null;

  return (
    <>
      <H3>{t("ProfileCompany.OBInconvenientWorkingHours")}</H3>

      <Text>{t("ProfileCompany.ObInfo")}</Text>

      {!showObForm && (
        <>
          <DetailsContainer />
          <ButtonStyled disabled={false} onClick={() => setShowObForm(true)}>
            {t("ProfileCompany.AddUnsocialHours")}
          </ButtonStyled>
        </>
      )}

      <ObSettingsForm
        setShowObForm={setShowObForm}
        showObForm={showObForm}
        currentWeekList={weekList}
      />

      {weekList.length > 0 && (
        <Text fontFamily={FontFamily.MontserratSemiBold}>
          {t("ProfileCompany.Weekdays")}
        </Text>
      )}

      {weekList.map((sameSettingsList, index) => (
        <Cell key={`week-${index}`}>
          <Text
            fontFamily={FontFamily.MontserratSemiBold}
            style={{ width: "300px" }}
          >
            {sameSettingsList
              .map((setting) =>
                typeof setting.weekday === "number"
                  ? t(`WeekDays.${OBSettingShortWeekDay[setting.weekday]}`)
                  : null
              )
              .filter(Boolean)
              .join(", ")}
          </Text>

          <Text>{`${moment(sameSettingsList[0].startTime, "HH:mm:ss").format(
            "HH:mm"
          )} - ${moment(sameSettingsList[0].endTime, "HH:mm:ss")
            .add(1, "second")
            .format("HH:mm")}`}</Text>

          <Text>
            {sameSettingsList[0].fixedValue != null
              ? `${sameSettingsList[0].fixedValue}Kr`
              : `${sameSettingsList[0].percentValue}%`}
          </Text>

          <CustomIcon
            style={{ marginLeft: "10px" }}
            className="icon"
            name="trashcan"
            size="20px"
            color={Color.Destructive}
            onClick={() => deleteObSetting(sameSettingsList)}
          />
        </Cell>
      ))}

      {dateList.length > 0 && (
        <Text
          fontFamily={FontFamily.MontserratSemiBold}
          style={{ marginTop: "32px" }}
        >
          {t("ProfileCompany.Dates")}
        </Text>
      )}

      {dateList.map((sameSettingsList, index) => (
        <Cell key={`date-${index}`}>
          <Text
            fontFamily={FontFamily.MontserratSemiBold}
            style={{ width: "300px" }}
          >
            {sameSettingsList[0].date ?? ""}
          </Text>

          <Text>{`${moment(sameSettingsList[0].startTime, "HH:mm:ss").format(
            "HH:mm"
          )} - ${moment(sameSettingsList[0].endTime, "HH:mm:ss")
            .add(1, "second")
            .format("HH:mm")}`}</Text>

          <Text>
            {sameSettingsList[0].fixedValue != null
              ? `${sameSettingsList[0].fixedValue}Kr`
              : `${sameSettingsList[0].percentValue}%`}
          </Text>

          <CustomIcon
            style={{ marginLeft: "10px" }}
            className="icon"
            name="trashcan"
            size="20px"
            color={Color.Destructive}
            onClick={() => deleteObSetting(sameSettingsList)}
          />
        </Cell>
      ))}
    </>
  );
};
